// TFVF Acknowledgements — 7 mandatory statements. All required; user
// must tick every checkbox to enable submit. Records session ref for
// audit.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';
import { API, authHeaders } from './tfvfShared';
import { useAuth } from '../../../contexts/AuthContext';

const TfvfAcks = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/tfvf/acknowledgements`, { headers: authHeaders(token) })
      .then(r => {
        setData(r.data);
        if (r.data?.mine?.statement_ids) {
          setChecked(Object.fromEntries(r.data.mine.statement_ids.map(id => [id, true])));
        }
      });
  }, [token]);

  const allSigned = data?.statements?.length > 0
    && data.statements.every(s => checked[s.id]);

  const submit = async () => {
    if (!allSigned) return;
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/tfvf/acknowledgements/submit`,
        {
          statement_ids: Object.keys(checked).filter(id => checked[id]),
          session_ref: `${window.location.hostname}::${Date.now()}`,
        },
        { headers: authHeaders(token) },
      );
      toast.success(`Acknowledgements signed (v${r.data.program_version})`);
      setTimeout(() => navigate('/training'), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submit failed');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--tf-forest)]/50" /></div>;

  const already = !!data.mine;

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="tfvf-acks-page">
      <button onClick={() => navigate('/training')} className="text-xs text-[color:var(--tf-muted)] hover:text-[color:var(--tf-forest)] inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to training
      </button>

      <div className="tf-card rounded-xl p-6">
        <p className="tf-eyebrow flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Platform acknowledgements · v{data.program_version}</p>
        <h1 className="text-2xl font-bold text-[color:var(--tf-forest)] mt-1">Please read and confirm</h1>
        <p className="text-sm text-[color:var(--tf-slate)] mt-1">
          These acknowledgements set out how VoiceFabric is used and what is expected of staff. Every statement is required. Your signature is timestamped and stored against your account with the current platform program version.
        </p>
        {already && (
          <div className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-300 text-emerald-800 text-sm">
            You already signed this version on {new Date(data.mine.signed_at).toLocaleString()}. Re-signing overwrites the record with today&apos;s timestamp.
          </div>
        )}
      </div>

      <div className="tf-card rounded-xl p-5 space-y-3">
        {data.statements.map((s) => (
          <label
            key={s.id}
            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer text-sm ${
              checked[s.id] ? 'border-[color:var(--tf-gold)] bg-[color:var(--tf-parchment)]' : 'border-[color:var(--tf-forest)]/10 hover:bg-[color:var(--tf-parchment)]/50'
            }`}
            data-testid={`ack-${s.id}`}
          >
            <input
              type="checkbox"
              checked={!!checked[s.id]}
              onChange={e => setChecked(c => ({ ...c, [s.id]: e.target.checked }))}
              className="mt-0.5 accent-[color:var(--tf-gold)]"
            />
            <div>
              <p className="text-[10px] font-bold text-[color:var(--tf-gold-2)] uppercase tracking-wider">{s.id}</p>
              <p className="text-[color:var(--tf-slate)] mt-0.5">{s.text}</p>
            </div>
          </label>
        ))}
        <button
          onClick={submit}
          disabled={!allSigned || saving}
          className="tf-btn-primary w-full py-3 rounded-lg font-semibold text-sm disabled:opacity-50"
          data-testid="tfvf-ack-submit"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
          Sign & continue
        </button>
      </div>
    </div>
  );
};

export default TfvfAcks;
